import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'

import { SECTION_ICONS } from '../components/icons'
import { useSession } from '../lib/session'
import { MOBILE, useMediaQuery } from '../lib/useMediaQuery'
import { UserMenu } from './UserMenu'

/**
 * The persistent shell — `APPLICATION_SPEC.md` §2.2.
 *
 * **Sidebar for the seven sections, SS1's tab row inside each one.** Seven
 * sections do not fit one tab row, and four of them are contracted workstreams
 * (`design-system/DECISIONS.md`, decision 3).
 *
 * Ungranted sections are **hidden, not disabled** — decided 7 Sep. A locked
 * Revenue item would advertise revenue to people deliberately denied it, which
 * cuts against Jesse's *"not the revenue stuff"*. The list comes from the
 * server's `sections`, derived from the same `has_grant` logic the route
 * dependencies use, so the nav and the API cannot disagree.
 *
 * **Two sidebars, one component.** On a desktop it is a persistent column that
 * can collapse to the 64px rail the tokens already define
 * (`--sidebar-collapsed`); on a phone it is an overlay drawer with a backdrop.
 * Those differ in behaviour, not just in width — the drawer traps nothing but
 * must close on Escape, on backdrop press and on navigation, none of which a
 * persistent column should do.
 */

interface Section {
  key: string
  to: string
  label: string
  group: 'Dashboards' | 'Data' | 'Operations'
}

// `/account` sits in the user menu. DESIGN-SYSTEM.md §7.
//
// **`/legacy` is a section as of 9 Sep** (Nilay), reversing the earlier call
// that it was reachable from Connections only. The argument for hiding it was
// that it is not our app — but that is exactly why it needs its own entry:
// somebody looking for a number they remember from the old Tableau portal has
// no reason to go looking under *Connections* for it, and the frozen
// dashboards are a contracted deliverable (SOW workstream 7), not an
// implementation detail of pipeline health.
export const SECTIONS: Section[] = [
  { key: 'stories', to: '/stories', label: 'Stories', group: 'Dashboards' },
  { key: 'spotlights', to: '/spotlights', label: 'Spotlights', group: 'Dashboards' },
  { key: 'channels', to: '/channels', label: 'Channel Overview', group: 'Dashboards' },
  { key: 'revenue', to: '/revenue', label: 'Revenue', group: 'Data' },
  { key: 'raw-data', to: '/raw-data', label: 'Raw Data', group: 'Data' },
  { key: 'connections', to: '/connections', label: 'Connections', group: 'Operations' },
  { key: 'legacy', to: '/legacy', label: 'Frozen Tableau', group: 'Operations' },
  { key: 'users', to: '/admin/users', label: 'Users', group: 'Operations' },
]

const GROUPS = ['Dashboards', 'Data', 'Operations'] as const
const COLLAPSE_KEY = 'pca-sidebar-collapsed'

export function AppShell() {
  const { me, signOut } = useSession()
  const location = useLocation()
  const isMobile = useMediaQuery(MOBILE)

  // Desktop: a remembered preference. A per-viewer convenience, so
  // localStorage is the right home — and it must survive being unreadable.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1'
    } catch {
      return false
    }
  })
  // Mobile: never remembered. A drawer that opened by itself on every page load
  // would cover the content it is there to navigate to.
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
    } catch {
      // Private window or blocked site data. The preference just does not persist.
    }
  }, [collapsed])

  // Navigating is the signal the drawer has done its job.
  useEffect(() => setDrawerOpen(false), [location.pathname])

  useEffect(() => {
    if (!isMobile || !drawerOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    // Stop the page behind the drawer scrolling under the finger.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
    }
  }, [isMobile, drawerOpen])

  const visible = SECTIONS.filter((section) => me?.sections.includes(section.key))

  // The freshness header. Always on, because a dashboard that silently shows
  // stale numbers is the failure this project is most exposed to.

  const railed = !isMobile && collapsed
  const width = railed ? 'var(--sidebar-collapsed, 64px)' : 'var(--sidebar-width, 236px)'

  const sidebar = (
    <nav
      aria-label="Sections"
      id="app-sidebar"
      className="flex shrink-0 flex-col px-3 py-4"
      style={{
        width,
        background: 'var(--sidebar-bg, var(--bg-surface))',
        borderRight: '1px solid var(--sidebar-border, var(--border))',
        ...(isMobile
          ? {
              position: 'fixed',
              inset: '0 auto 0 0',
              zIndex: 'var(--z-overlay, 400)' as unknown as number,
              boxShadow: '0 0 40px rgba(0,0,0,0.25)',
            }
          : {
              position: 'sticky',
              top: 0,
              height: '100vh',
              // `position: sticky` creates a stacking context, so the account
              // popover's own z-index can only order it *within the sidebar*.
              // Without a z-index here the main column — later in the DOM —
              // painted over the open menu, and chart labels showed through it.
              zIndex: 'var(--z-sticky, 200)' as unknown as number,
            }),
      }}
    >
      {/* The collapse control sits at the top, beside the wordmark: it acts on
          the whole sidebar, and a control for a region belongs at that region's
          start, where it is found without scanning past seven nav items. */}
      <div className={`mb-6 flex items-center gap-2 ${railed ? 'flex-col' : 'px-2'}`}>
        {/* An SVG wordmark is still owed — only .webp and .jpeg were supplied,
            and there is no brand guide anywhere in ReferenceFiles. */}
        {!railed && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold tracking-tight">Phony Content</span>
            <span className="block text-xs" style={{ color: 'var(--text-label)' }}>
              Analytics
            </span>
          </span>
        )}

        {!isMobile && (
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-expanded={!collapsed}
            aria-controls="app-sidebar"
            aria-label={railed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="rail-item flex items-center justify-center"
            style={{
              width: '2rem',
              height: '2rem',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            <svg
              aria-hidden="true"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: railed ? 'rotate(180deg)' : 'none' }}
            >
              <path d="M14 6l-6 6 6 6" />
              <path d="M18.5 5v14" />
            </svg>
            {railed && <span className="rail-tip">Expand sidebar</span>}
          </button>
        )}

        {railed && (
          <span className="text-xs font-bold" style={{ color: 'var(--text-label)' }}>
            PC
          </span>
        )}

        {isMobile && (
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
            className="ml-auto px-2 py-1 text-sm"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {GROUPS.map((group) => {
        const items = visible.filter((section) => section.group === group)
        if (!items.length) return null
        return (
          <div key={group} className="mb-5">
            {/* The group heading is hidden from sight on the rail but kept for
                a screen reader, so the nav keeps its structure at both widths. */}
            <h2
              className={
                railed
                  ? 'sr-only'
                  : 'mb-2 px-2 text-[0.6875rem] font-semibold uppercase'
              }
              style={railed ? undefined : { color: 'var(--text-label)', letterSpacing: '0.08em' }}
            >
              {group}
            </h2>
            <ul className="m-0 list-none p-0">
              {items.map((section) => (
                <li key={section.key}>
                  <NavLink
                    to={section.to}
                    // The label is the accessible name in both states — the
                    // icon is decorative, so the rail is not a row of unnamed
                    // links to a screen reader.
                    aria-label={railed ? section.label : undefined}
                    className={`rail-item mb-0.5 flex items-center gap-2.5 py-1.5 text-sm ${
                      railed ? 'justify-center' : 'px-2'
                    }`}
                    style={({ isActive }) => ({
                      // Mint owns nav state; --brand-ink is the readable mint.
                      background: isActive
                        ? 'var(--sidebar-item-active, var(--brand-tint))'
                        : 'transparent',
                      color: isActive ? 'var(--brand-ink)' : 'var(--text-secondary)',
                      fontWeight: isActive ? 600 : 400,
                      borderRadius: 'var(--radius-md)',
                      textDecoration: 'none',
                      // A 40px-tall target on the rail: an icon-only hit area
                      // must not shrink to the glyph.
                      minHeight: railed ? '2.5rem' : undefined,
                    })}
                  >
                    {(() => {
                      const Icon = SECTION_ICONS[section.key]
                      return Icon ? <Icon className="shrink-0" /> : null
                    })()}
                    {railed ? (
                      // Hover *and* keyboard focus, which `title` cannot do.
                      <span className="rail-tip" aria-hidden="true">
                        {section.label}
                      </span>
                    ) : (
                      <span className="truncate">{section.label}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )
      })}

      <UserMenu me={me} onSignOut={() => void signOut()} collapsed={railed} />

    </nav>
  )

  return (
    <div className="flex min-h-screen">
      {isMobile ? (
        drawerOpen && (
          <>
            <div
              // Not a button: it is a dismissal surface, and announcing it as a
              // control would put a nameless button in the tab order. Escape
              // and the labelled close button are the accessible paths.
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.45)',
                zIndex: 'var(--z-overlay, 400)' as unknown as number,
              }}
            />
            {sidebar}
          </>
        )
      ) : (
        sidebar
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* 🔴 **Mobile only, because on desktop it held nothing.**
            This bar kept its padding, its surface and its bottom border long
            after everything inside it was removed — the page title on 10 Sep
            (the view owns its own heading) and the *Pipelines Reachable* badge
            with D-6. What was left rendered as an empty white strip across the
            top of every page, which is what Nilay asked about. On mobile it
            still carries the drawer button and earns its place. */}
        {isMobile && (
        <header
          className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6"
          style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}
        >
          {isMobile && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              aria-expanded={drawerOpen}
              aria-controls="app-sidebar"
              className="px-2 py-1"
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                // 44px is the touch-target floor; the icon alone is not.
                minWidth: '2.25rem',
                minHeight: '2.25rem',
              }}
            >
              ☰
            </button>
          )}
          {/* No page title here. The view owns its own heading, directly
              above the controls that scope it — repeating it in the shell put
              two identical headings 20px apart.
              
              🔴 **The "Pipelines Reachable" badge was removed 10 Sep (D-6).**
              It read `/api/health`, which checks **only whether the database
              connects** — it never touched Snap, ClickUp or thumbnails. So it
              sat green for the three days the asset-list and lifetime pipelines
              were silently not running (D-3). Its failure branch was dead code
              besides: a degraded health check returns 503, which `request()`
              throws on, so `health.data` was never populated and the red state
              could not render — the badge simply vanished instead.
              `/connections` reports real per-source state and is one click
              away. */}
        </header>
        )}

        {/* The landmark's accessible name, kept outside the bar so removing the
            bar on desktop does not remove it from a screen reader. */}
        <span className="sr-only">
          {visible.find((section) => location.pathname.startsWith(section.to))?.label ??
            'Overview'}
        </span>


        <main className="min-w-0 flex-1 px-4 py-5 sm:px-6 sm:py-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
